import { useMemo, useState } from 'react';
// Aliased: `Check` in this app is a restaurant check, not a tick.
import { Check as CheckIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CheckHeader } from '@/features/check/CheckHeader';
import { PaymentMark } from './PaymentMarks';
import { PAYMENT_METHOD_LABEL_KEYS } from './labels';
import { availablePaymentMethods, type PaymentMethodId, type WalletCapabilities } from './availability';
import { isCardOnly } from '@/payment/cardOnly';
import { formatMoney } from '@/lib/money';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import type { Check, Location } from '@/api/types';

interface PaymentMethodScreenProps {
    location: Location;
    check: Check;
    /** The tip the guest settled on, in minor units. Shown in the total they are about to authorise. */
    tipCents: number;
    onContinue?: (method: PaymentMethodId) => void;
    onBack?: () => void;
    /** What the device can present, resolved by the route before this renders. */
    capabilities: WalletCapabilities;
}

/**
 * Where the guest picks how to pay.
 *
 * Only ever reached when there is something to pick. The route decides that with `requiresMethodSelection` -
 * a location offering no wallets, or one whose only wallet cannot run on this phone, leaves card as the sole
 * option and this screen is skipped entirely rather than shown with one row on it.
 *
 * The screen still derives its own list rather than taking one, so it cannot disagree with the decision that
 * routed the guest here.
 */
export function PaymentMethodScreen({
    location,
    check,
    tipCents,
    onContinue,
    onBack,
    capabilities,
}: PaymentMethodScreenProps) {
    const { t } = useTranslation();
    const { currency, locale } = location;

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    /*
     * `isCardOnly()` reads storage rather than arriving as a prop, and is read here as well as in the tip
     * step that routes past this screen. Both, deliberately: the tip step decides whether this screen is
     * reached at all, but a guest can arrive at `/payment` directly - a back gesture, a reload, a restored
     * tab - and this screen must not offer wallets the tablet cannot open just because nothing routed it.
     */
    const methods = useMemo(
        () => availablePaymentMethods(location, capabilities, isCardOnly()),
        [location, capabilities],
    );

    const [selected, setSelected] = useState<PaymentMethodId>(() => methods[0] ?? 'card');

    // A method that is no longer offered must not stay selected - the same clamp the tip screen needs.
    const active = methods.includes(selected) ? selected : (methods[0] ?? 'card');

    const totalCents = check.totalCents + tipCents;

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <CheckHeader location={location} check={check} />

                <main className="px-5 pb-44">
                    <h2 className="text-lg font-semibold tracking-tight">{t('payment.title')}</h2>

                    <fieldset className="mt-4">
                        {/*
                          * Native radios behind the styled cards, as on the tip screen: arrow-key navigation
                          * and the "2 of 3" announcement come for free, and the focus ring lands on the
                          * visible card rather than on a screen-reader-only input.
                          */}
                        <legend className="sr-only">{t('payment.optionsLabel')}</legend>

                        <div className="flex flex-col gap-3">
                            {methods.map((method) => (
                                <label key={method} className="block cursor-pointer">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        className="peer sr-only"
                                        checked={method === active}
                                        onChange={() => setSelected(method)}
                                    />
                                    {/*
                                      * The border is 2px in both states so selecting one does not nudge the
                                      * layout; only its colour changes.
                                      *
                                      * Selection is carried by four things at once - border colour, a tinted
                                      * ground, the label colour, and a filled tick. That is partly emphasis
                                      * and partly WCAG 1.4.1: the tick means the chosen row is still obvious
                                      * to someone who cannot separate the brand colour from the neutral one,
                                      * and the tint survives a brand whose primary is barely darker than the
                                      * card it sits on.
                                      */}
                                    <span className="relative flex flex-col gap-1.5 rounded-xl border-2 border-border bg-card px-5 py-2 pr-14 text-foreground transition-colors peer-checked:border-primary peer-checked:bg-primary/10 peer-checked:text-primary peer-checked:[&_[data-indicator]]:border-primary peer-checked:[&_[data-indicator]]:bg-primary peer-checked:[&_[data-tick]]:opacity-100 peer-focus-visible:outline-solid peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-foreground">
                                        <PaymentMark method={method} />
                                        <span className="text-lg leading-tight font-semibold tracking-tight">
                                            {t(PAYMENT_METHOD_LABEL_KEYS[method])}
                                        </span>

                                        {/*
                                          * Reads as the radio it actually is. Empty ring unselected, filled
                                          * with a tick when chosen - and it lives in the dead space on the
                                          * right that the mark and label never used.
                                          */}
                                        <span
                                            data-indicator
                                            aria-hidden="true"
                                            className="absolute top-1/2 right-4 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border-2 border-border transition-colors"
                                        >
                                            <CheckIcon
                                                data-tick
                                                strokeWidth={3}
                                                className="size-3.5 text-primary-foreground opacity-0 transition-opacity"
                                            />
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    {/*
                      * Names the step it actually returns to. The route sends a guest back to the tip step
                      * unless this location does not take tips - that one forwards straight here, so it
                      * navigates to the check instead. The label followed the tip wording either way, which
                      * offered to take a guest back to a screen their location never shows.
                      */}
                    {onBack && (
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={onBack}
                                className="inline-flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4"
                            >
                                {t(check.tipsEnabled ? 'payment.back' : 'payment.backToCheck')}
                            </button>
                        </div>
                    )}
                </main>
            </div>

            <div className="pointer-events-none fixed inset-x-0 bottom-0">
                <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                    <div className="h-8 bg-gradient-to-t from-background/90 to-transparent" />

                    <footer className="pointer-events-auto border-t border-border bg-background/90 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-md">
                        <div className="tabular mb-3.5 flex items-baseline justify-between gap-3">
                            <span className="text-[0.9375rem] font-medium text-muted-foreground">
                                {t('check.total')}
                            </span>
                            <span className="text-[1.75rem] leading-none font-semibold tracking-tight">
                                {formatMoney(totalCents, currency, locale)}
                            </span>
                        </div>

                        <Button
                            size="lg"
                            className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                            onClick={() => onContinue?.(active)}
                        >
                            {t('payment.continue')}
                        </Button>
                    </footer>
                </div>
            </div>
        </div>
    );
}
